import { useEffect, useRef } from 'react';
import { Close, CloudUpload, Shield } from '@mui/icons-material';
import { ComplianceItem } from '../../data/types';

type ComplianceUploadModalProps = {
  item: ComplianceItem;
  onClose: () => void;
  onUpload: (file: File) => void | Promise<void>;
  isUploading?: boolean;
  onOpenLegal?: () => void;
};

/** Modal for uploading compliance documents with inline legal context. */
export const ComplianceUploadModal = ({
  item,
  onClose,
  onUpload,
  isUploading = false,
  onOpenLegal,
}: ComplianceUploadModalProps) => {
  const uploadInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const canUpload = item.status === 'pending' || item.status === 'expired';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compliance-upload-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-white/70 backdrop-blur-[2px]"
        onClick={onClose}
        aria-label="Close"
      />

      <div className="relative flex max-h-[90vh] w-full max-w-[560px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_18px_48px_rgba(1,56,71,0.14)]">
        <div className="flex items-start justify-between px-8 py-6">
          <div>
            <h2 id="compliance-upload-title" className="font-raleway text-xl font-bold text-[#111111]">
              {item.title}
            </h2>
            <p className="mt-1 font-open-sans text-sm text-[#808080]">Upload your document</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[#737373] transition-colors hover:bg-[#f8f8f8]"
            aria-label="Close"
          >
            <Close />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-8 pb-6">
          <p className="font-open-sans text-sm text-[#4D4D4D]">{item.description}</p>

          <section className="rounded-xl border border-[#5DB6CE]/20 bg-[#EFF8FA]/60 p-4">
            <div className="flex gap-3">
              <Shield className="mt-0.5 text-primary-400" />
              <div>
                <h3 className="font-raleway text-sm font-semibold text-[#013847]">
                  Why is this required?
                </h3>
                <p className="mt-2 font-open-sans text-sm leading-relaxed text-[#013847]/80">
                  {item.legal.summary}
                </p>
                {item.legal.regionNote && (
                  <p className="mt-3 font-open-sans text-sm leading-relaxed text-[#013847]/70">
                    {item.legal.regionNote}
                  </p>
                )}
                {onOpenLegal && (
                  <button
                    type="button"
                    onClick={onOpenLegal}
                    className="mt-3 font-open-sans text-sm font-medium text-primary-200 underline"
                  >
                    View full legal details
                  </button>
                )}
              </div>
            </div>
          </section>

          {item.legal.references?.length > 0 && (
            <section>
              <h3 className="font-raleway text-sm font-semibold text-[#4D4D4D]">Legal references</h3>
              <ul className="mt-3 space-y-2">
                {item.legal.references.slice(0, 3).map((reference) => (
                  <li
                    key={`${reference.label}-${reference.citation}`}
                    className="rounded-lg border border-[#E9E9E9] bg-[#fafafa] p-3"
                  >
                    <p className="font-raleway text-sm font-semibold text-[#013847]">
                      {reference.label}
                    </p>
                    <p className="mt-1 font-open-sans text-sm text-[#4D4D4D]">{reference.citation}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {canUpload ? (
            <div className="rounded-xl border border-dashed border-[#E9E9E9] bg-[#fafafa] p-6 text-center">
              <input
                ref={uploadInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                className="sr-only"
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (file) {
                    await onUpload(file);
                    onClose();
                  }
                }}
              />
              <CloudUpload className="mx-auto text-[#5DB6CE]" sx={{ fontSize: 36 }} />
              <p className="mt-3 font-open-sans text-sm text-[#4D4D4D]">
                PDF, DOC, DOCX, or image files
              </p>
              <button
                type="button"
                disabled={isUploading}
                className="mt-4 inline-flex h-10 items-center gap-2 rounded-lg bg-primary-200 px-5 font-open-sans text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                onClick={() => uploadInputRef.current?.click()}
              >
                <CloudUpload sx={{ fontSize: 18 }} />
                {isUploading ? 'Uploading…' : 'Choose file'}
              </button>
            </div>
          ) : (
            <div className="rounded-xl border border-[#E9E9E9] bg-[#fafafa] p-4 font-open-sans text-sm text-[#4D4D4D]">
              {item.uploadedFileName && (
                <p>
                  <span className="text-[#808080]">Uploaded:</span> {item.uploadedFileName}
                </p>
              )}
              {item.expiryDate && (
                <p className="mt-1">
                  <span className="text-[#808080]">Expires:</span> {item.expiryDate}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="px-8 py-6">
          <button
            type="button"
            className="h-12 w-full rounded-lg border border-[#E9E9E9] bg-white font-open-sans text-sm font-medium text-[#4D4D4D] transition-colors hover:bg-[#fafafa]"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComplianceUploadModal;
